import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import { Plus, Trash, X, ArrowRight, CheckCircle, XCircle } from "@phosphor-icons/react";

const empty = { code: "", name: "", type: "intermediario", active: true };

export default function Aseguradoras() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = can.manageInsurers(user);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const load = async () => {
    const r = await api.get("/aseguradoras");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      await api.post("/aseguradoras", form);
      load();
      setOpen(false);
      setForm(empty);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (e, a) => {
    e.stopPropagation();
    if (!confirm(`¿Eliminar ${a.name}?`)) return;
    await api.delete(`/aseguradoras/${a.id}`);
    load();
  };

  const directas = items.filter((a) => a.type === "directa");
  const intermediarios = items.filter((a) => a.type === "intermediario");

  return (
    <div data-testid="aseguradoras-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted">{canEdit ? "Administración" : "Consulta"}</div>
          <h1 className="font-display font-bold text-lg tracking-tight">Aseguradoras · {items.length}</h1>
        </div>
        {canEdit && (
          <button onClick={() => setOpen(true)} data-testid="new-aseguradora-button" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" weight="bold" /> Nueva aseguradora
          </button>
        )}
      </header>

      <div className="p-6 space-y-6 max-w-5xl">
        {[
          { title: "Clientes directos", subtitle: "Aseguradoras con las que trabajamos directamente", arr: directas },
          { title: "Intermediarios", subtitle: "Aseguradoras a través de plataformas/intermediarios", arr: intermediarios },
        ].map((g) => (
          <section key={g.title}>
            <div className="mb-3">
              <h2 className="font-display font-bold text-base tracking-tight">{g.title}</h2>
              <p className="text-xs text-muted">{g.subtitle}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {g.arr.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/admin/aseguradoras/${a.id}`)}
                  data-testid={`aseg-card-${a.code}`}
                  className="text-left border border-border rounded-md bg-white p-4 transition-all relative group cursor-pointer hover:border-foreground hover:shadow-sm hover:-translate-y-0.5 hover:bg-surface/50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-sm tracking-tight truncate">{a.name}</div>
                      <div className="font-mono text-[11px] text-muted mt-0.5">{a.code}</div>
                    </div>
                    {a.active ? (
                      <CheckCircle className="w-3.5 h-3.5 text-status-success-fg flex-shrink-0" weight="fill" title="Activa" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-muted flex-shrink-0" weight="fill" title="Inactiva" />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[11px] text-muted group-hover:text-foreground transition-colors inline-flex items-center gap-1">
                      Ver detalle <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" weight="bold" />
                    </span>
                    <button
                      onClick={(e) => del(e, a)}
                      data-testid={`del-aseg-${a.code}`}
                      className={`opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-status-error-fg ${canEdit ? "" : "hidden"}`}
                    >
                      <Trash className="w-3 h-3" weight="bold" />
                    </button>
                  </div>
                </button>
              ))}
              {g.arr.length === 0 && (
                <div className="col-span-full text-xs text-muted py-4">Ninguna registrada.</div>
              )}
            </div>
          </section>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg tracking-tight">Nueva aseguradora</h2>
              <button onClick={() => setOpen(false)} className="text-muted hover:text-foreground"><X className="w-4 h-4" weight="bold" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="aseg-name"
                  placeholder="Ej: Reale Seguros"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1">Código</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  data-testid="aseg-code"
                  placeholder="REALE"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                />
                <p className="text-[11px] text-muted mt-1">Identificador único en mayúsculas (sin espacios).</p>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1">Tipo</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="aseg-type"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                >
                  <option value="directa">Cliente directo</option>
                  <option value="intermediario">Intermediario</option>
                </select>
              </div>
              {err && <div className="text-xs text-status-error-fg bg-status-error-bg border border-status-error-bd px-2 py-1.5 rounded">{err}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
              <button
                onClick={save}
                data-testid="save-aseg"
                disabled={!form.name.trim() || !form.code.trim()}
                className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <Plus className="w-3 h-3" weight="bold" />Crear aseguradora
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
